import { ArticleType, dateToLocaleDateString, FlightArticleDetails } from '@nemuru-eng/js-api-client';
import airportData from 'aircodes';

import { FlightFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Flight/types/FlightFormItem';
import { Item } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/Item';

export class FlightItem extends Item<FlightFormItem> {
  constructor(data: FlightFormItem) {
    if (data.reference && data.departureAirport && data.destinationAirport) {
      data = {
        ...data,
        description: FlightItem.description(data),
      };
    }
    super(data);
  }

  protected isCompleted(): boolean {
    return (
      !!this.data.departureDate &&
      !!this.data.departureAirport &&
      !!this.data.destinationAirport &&
      !!this.data.reference &&
      !!this.data.passengers.length &&
      (this.data.isRoundTrip ? !!this.data.returnDate : true)
    );
  }

  protected toForm(): FlightFormItem {
    return {
      ...this.toMinimalFormItem(),
      reference: this.data.reference,
      departureDate: this.data.departureDate,
      departureAirport: this.data.departureAirport,
      destinationAirport: this.data.destinationAirport,
      passengers: this.data.passengers.map((passenger) => {
        return {
          documentNumber: passenger.documentNumber,
        };
      }),
      isRoundTrip: this.data.isRoundTrip,
      returnDate: this.data.returnDate,
      returnReference: this.data.returnReference,
    } as FlightFormItem;
  }

  protected toDto(): object {
    return {
      name: FlightItem.fullDescription(this.data),
      reference: this.data.reference.trim().toUpperCase(),
      description: FlightItem.fullDescription(this.data),
      departureDate: this.data.departureDate,
      segments: [
        {
          iataCarrierCode: this.data.reference.trim().toUpperCase().slice(0, 2),
          iataDepartureCode: this.data.departureAirport.iata,
          iataDestinationCode: this.data.destinationAirport.iata,
          iataFlightCode: this.data.reference.trim(),
          departureTime: this.data.departureDate,
        },
        ...(this.data.isRoundTrip
          ? [
              {
                iataCarrierCode: this.data.returnReference?.trim().toUpperCase().slice(0, 2),
                iataDepartureCode: this.data.destinationAirport.iata,
                iataDestinationCode: this.data.departureAirport.iata,
                iataFlightCode: this.data.returnReference?.trim().toUpperCase(),
                departureTime: this.data.returnDate,
              },
            ]
          : []),
      ],
      passengers: this.data.passengers.map((passenger) => {
        return {
          firstName: 'unknown',
          lastName: 'unknown',
          documentNumber: passenger.documentNumber,
        };
      }),
    };
  }

  public static fromOrderLineDto(units: number, article: FlightArticleDetails): FlightItem {
    const iataDepartureCode = article.segments?.[0]?.iataDepartureCode ?? article.iataDepartureCode;
    const iataDestinationCode = article.segments?.[0]?.iataDestinationCode ?? article.iataDestinationCode;

    const formData: FlightFormItem = {
      // Minimal form item fields
      units: units.toString(),
      vat: '21', // FIXME: Default VAT
      price: article.price.amount.toFixed(2).toString(),
      category: article.category,

      // ArticleType-specific fields
      type: ArticleType.FLIGHT,
      departureDate: article.departureDate as Date,
      reference: article.reference || '',
      departureAirport: {
        iata: iataDepartureCode ?? '',
        city: airportData.getAirportByIata(iataDepartureCode).city ?? '',
      },
      destinationAirport: {
        iata: iataDestinationCode ?? '',
        city: airportData.getAirportByIata(iataDestinationCode).city ?? '',
      },
      passengers:
        article.passengers && article.passengers.length > 0
          ? (article.passengers.map((passenger) => ({
              documentNumber: passenger.documentNumber || '',
            })) as [{ documentNumber: string }, ...{ documentNumber: string }[]])
          : [
              {
                documentNumber: '',
              },
            ],
      isRoundTrip: !!article.segments?.[1],
      returnDate: article.segments?.[1]?.departureTime as Date | null,
      returnReference: article.segments?.[1]?.iataFlightCode || '',
    };

    return new FlightItem(formData);
  }

  private static description(data: FlightFormItem): string {
    return `${data.reference.trim().toUpperCase()} (${data.departureAirport.iata} - ${data.destinationAirport.iata})`;
  }

  private static fullDescription(data: FlightFormItem): string {
    return `${data.departureAirport.city} (${data.departureAirport.iata}), (${dateToLocaleDateString(
      data.departureDate,
    )}) - ${data.destinationAirport.city} (${data.destinationAirport.iata})`;
  }
}
